import {
  AccessorialCode,
  BillingTerms,
  Direction,
  DomainReference,
  EquipmentType,
  GLCode,
  Mode,
  Money,
  TaggedEntity,
  UserRef,
} from '@rygen/rygen-ui/model';

import { CancellationReason } from '../CancellationReason';
import { FreightAllowance } from '../FreightAllowance';
import { ReferenceNumber } from '../ReferenceNumber';
import { ShipmentStopType } from '../shipment/ShipmentStopType';

import { OrderBillTo } from './OrderBillTo';
import { OrderLineItem } from './OrderLineItem';
import { OrderShippingPoint } from './OrderShippingPoint';
import { OrderStatus } from './OrderStatus';

type ConstructableOrder = Partial<Order> & {
  stops?: [origin: OrderShippingPoint, destination: OrderShippingPoint];
};

export class Order implements TaggedEntity {
  id?: number;
  owner?: DomainReference;
  status?: OrderStatus;
  origin: OrderShippingPoint = new OrderShippingPoint({
    stopType: ShipmentStopType.PICKUP,
  });
  destination: OrderShippingPoint = new OrderShippingPoint({
    stopType: ShipmentStopType.DROP_OFF,
  });
  lineItems: OrderLineItem[] = [];
  referenceNumbers: ReferenceNumber[] = [];
  direction?: Direction;
  billingTerms?: BillingTerms;
  billTo?: OrderBillTo;
  internalNotes?: string;
  carrierNotes?: string;
  equipmentType?: EquipmentType;
  requestedAccessorials: AccessorialCode[] = [];
  glCodes?: GLCode[] = [];
  totalMileage?: number;
  tags: string[] = [];
  assignedUsers: UserRef[] = [];
  autoGlCodeOverride?: GLCode;
  swc = false;
  customerRouted = false;
  hot = false;
  cancellationReason?: CancellationReason;
  cancellationComments?: string;
  freightAllowance?: FreightAllowance;
  skipAutoTender = false;
  valueOfGoods?: Money;
  requestedMode?: Mode;
  source?: string;
  createdBy?: string;
  createdDate?: Date;

  readonly type = 'Order';

  constructor(opts?: ConstructableOrder) {
    if (opts?.id != null) {
      this.id = opts.id;
    }
    if (opts?.owner != null) {
      this.owner = new DomainReference(opts.owner);
    }
    if (opts?.status != null) {
      this.status = opts.status;
    }

    const [origin, destination] = opts?.stops ?? [];
    if (opts?.origin ?? origin) {
      this.origin = new OrderShippingPoint({
        ...(opts?.origin ?? origin),
        stopType: ShipmentStopType.PICKUP,
      });
    }
    if (opts?.destination ?? destination) {
      this.destination = new OrderShippingPoint({
        ...(opts?.destination ?? destination),
        stopType: ShipmentStopType.DROP_OFF,
      });
    }
    if (opts?.lineItems != null) {
      this.lineItems = opts.lineItems.map((val) => new OrderLineItem(val));
    } else {
      this.lineItems = [new OrderLineItem()];
    }
    if (opts?.referenceNumbers != null) {
      this.referenceNumbers = opts.referenceNumbers.map(
        (val) => new ReferenceNumber(val),
      );
    }
    if (opts?.direction != null) {
      this.direction = opts.direction;
    }
    if (opts?.billingTerms != null) {
      this.billingTerms = opts.billingTerms;
    }
    if (opts?.billTo != null) {
      this.billTo = new OrderBillTo(opts.billTo);
    }
    if (opts?.internalNotes != null) {
      this.internalNotes = opts.internalNotes;
    }
    if (opts?.carrierNotes != null) {
      this.carrierNotes = opts.carrierNotes;
    }
    if (opts?.equipmentType != null) {
      this.equipmentType = new EquipmentType(opts.equipmentType);
    }
    if (opts?.requestedAccessorials != null) {
      this.requestedAccessorials = opts.requestedAccessorials.map(
        (val) => new AccessorialCode(val),
      );
    }
    if (opts?.glCodes != null) {
      this.glCodes = opts.glCodes.map((val) => new GLCode(val));
    }
    if (opts?.totalMileage != null) {
      this.totalMileage = opts.totalMileage;
    }
    if (opts?.tags != null) {
      this.tags = [...opts.tags];
    }
    if (opts?.assignedUsers != null) {
      this.assignedUsers = opts.assignedUsers.map((val) => new UserRef(val));
    }
    if (opts?.autoGlCodeOverride != null) {
      this.autoGlCodeOverride = opts.autoGlCodeOverride;
    }
    if (opts?.swc != null) {
      this.swc = opts.swc;
    }
    if (opts?.customerRouted != null) {
      this.customerRouted = opts.customerRouted;
    }
    if (opts?.hot != null) {
      this.hot = opts.hot;
    }
    if (opts?.cancellationReason != null) {
      this.cancellationReason = new CancellationReason(opts.cancellationReason);
    }
    if (opts?.cancellationComments != null) {
      this.cancellationComments = opts.cancellationComments;
    }
    if (opts?.freightAllowance != null) {
      this.freightAllowance = new FreightAllowance(opts?.freightAllowance);
    }
    if (opts?.skipAutoTender != null) {
      this.skipAutoTender = opts.skipAutoTender;
    }
    if (opts?.requestedMode != null) {
      this.requestedMode = opts.requestedMode;
    }
    if (opts?.source != null) {
      this.source = opts.source;
    }
    if (opts?.createdBy != null) {
      this.createdBy = opts.createdBy;
    }
    if (opts?.createdDate != null) {
      this.createdDate = opts.createdDate;
    }
    this.valueOfGoods = opts?.valueOfGoods
      ? new Money(opts.valueOfGoods)
      : undefined;
  }

  get isHazardous(): boolean {
    return this.lineItems.some((li) => li.isHazmat);
  }

  get stops(): [origin: OrderShippingPoint, destination: OrderShippingPoint] {
    return [this.origin, this.destination];
  }

  set stops([origin, destination]: [
    origin: OrderShippingPoint,
    destination: OrderShippingPoint,
  ]) {
    this.origin = origin;
    this.destination = destination;
  }
}
